import { useEffect, useRef, useState } from 'react';
import parts from '../parts/parts.module.css';
import styles from './sale.module.css';

/** The old copy route, which also works in iframes that aren't allowed the Clipboard API. */
function copyWithSelection(text: string): boolean {
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    try {
        return document.execCommand('copy');
    } catch {
        return false;
    } finally {
        field.remove();
    }
}

/** A promo code with a one-click copy button. If copying is blocked, the code is selected instead. */
export function PromoCode({ code, compact }: { code: string; compact?: boolean }) {
    const [copied, setCopied] = useState(false);
    const codeRef = useRef<HTMLElement>(null);

    useEffect(() => {
        if (!copied) return;
        const id = window.setTimeout(() => setCopied(false), 2000);
        return () => window.clearTimeout(id);
    }, [copied]);

    async function copy() {
        // navigator.clipboard is missing on insecure pages and may be denied in iframes.
        const ok = await (navigator.clipboard?.writeText(code) ?? Promise.reject()).then(
            () => true,
            () => copyWithSelection(code),
        );
        if (ok) return setCopied(true);
        // Last resort: select the code so the visitor can copy it themselves.
        const selection = window.getSelection();
        if (selection && codeRef.current) selection.selectAllChildren(codeRef.current);
    }

    return (
        <div className={[styles.promo, compact && styles.promoCompact].filter(Boolean).join(' ')}>
            <span className={styles.promoLabel}>Code</span>
            <code ref={codeRef} className={styles.promoCode}>
                {code}
            </code>
            <button
                type="button"
                className={styles.promoCopy}
                onClick={copy}
                aria-label={`Copy code ${code}`}
            >
                {copied ? 'Copied' : 'Copy'}
            </button>
            <span className={parts.visuallyHidden} role="status">
                {copied ? 'Code copied' : ''}
            </span>
        </div>
    );
}
